/*
 * OSI-808: минимальные ширины колонок ОСВ — ОДНО место для всех четырёх экранов-потребителей
 * OsvGrid («ОСВ ОСИ», «Сальдо ОСИ», «ОСВ по помещению», «Сальдо квартиры»), чтобы их вид
 * не разъехался: колонки там объявлены в четырёх разных файлах (в двух — константой уровня
 * модуля, в двух — через useMemo), и держать в них четыре независимых числа = гарантированный
 * рассинхрон при первой же правке.
 *
 * Зачем понадобилось: при minWidth: 50 у денежных колонок суммы крупных домов обрезались
 * («65 270 007…»), а при minWidth: 160 у первой текстовой колонки обрезались названия категорий
 * услуг («Текущие взносы н…»). Названия категорий попадают в первую колонку на ВСЕХ четырёх
 * экранах: на «ОСВ/Сальдо ОСИ» — в `flat` у строк level 1 и у закреплённых строк итогов,
 * на вкладках «Помещения» — в `period` у строк level 1 (см. getOsvRowsByFlat в helpers/osv.ts:78).
 * Поэтому обе ширины применяются на всех четырёх экранах одинаково.
 *
 * ВАЖНО про подсказку с полным значением: отдельный код НЕ нужен и НЕ добавлен.
 * @mui/x-data-grid@5.17.26 уже рендерит содержимое ячейки как
 * `<div title={formattedValue} class="MuiDataGrid-cellContent">` (components/cell/GridCell.js:181-187,
 * valueToRender = formattedValue ?? value), т.е. нативная подсказка с ПОЛНЫМ отформатированным
 * значением есть у каждой ячейки, где не задан renderCell — а его нет ни у одной денежной
 * и ни у одной текстовой колонки ОСВ. У заголовков то же самое: GridColumnHeaderTitle вешает title,
 * когда подпись не влезла (isOverflown). Заворачивать ячейки в renderCell/MUI Tooltip было бы хуже:
 * это сняло бы класс cellContent (а вместе с ним и многоточие overflow/text-overflow из
 * GridRootStyles.js:309-312) и добавило бы по компоненту-обёртке на каждую ячейку грида.
 */

/*
 * Денежные колонки (totalBegin/totalDebet/totalFixes/totalKredit/totalFine/totalEnd).
 * Арифметика (шрифт грида — Roboto 14px, паддинги ячейки `0 10px`, GridRootStyles.js:140):
 *  - цифра Roboto ≈ 0.56em ≈ 7.8px; разделитель разрядов ≈ 3.5px; запятая ≈ 3.3px;
 *  - девятизначная сумма «140 947 146,25» = 11 цифр (~86px) + 2 разделителя (~7px) + запятая (~3px) ≈ 96px;
 *  - + 20px паддингов = ~116px, + запас на знак «минус» у долгов и на другой системный шрифт → 125.
 * Восьмизначные суммы вроде «65 270 007,08» (~108px с паддингами) помещаются с запасом.
 * Десятизначные (>1 млрд ₸) всё равно не влезут — для них остаётся нативная подсказка.
 */
export const OSV_MONEY_COL_MIN_WIDTH = 125;

/*
 * Первая текстовая колонка (`flat` — № помещения / `period` — период).
 * Компромисс, а не «влезает всё»: полное «Текущие взносы на управление и содержание общего
 * имущества» (57 символов) потребовало бы ~450px, т.е. половину таблицы под одну колонку.
 * 170px — это ~150px текста ≈ 18-19 символов кириллицы. Проверено на реальных данных
 * (ОСИ Абилкайыр-хана 70-1, 7 категорий): различающая часть видна у ВСЕХ категорий —
 * «Целевой взнос», «Услуги паркинга», «Услуги котельной» помещаются целиком,
 * у остальных остаётся «Коммунальные услуг…», «Дополнительные усл…»,
 * «Текущие взносы на…», «Накопительные взно…» — категории различаются между собой
 * уже по первым 10-12 символам, так что обрезка не мешает их опознать.
 * Ниже 160px опускаться не стоит: «Услуги паркинга» и «Услуги котельной» расходятся
 * только с 8-го символа, и при совсем узкой колонке их станет не отличить.
 * Полное значение доступно в нативной подсказке (атрибут title у ячейки — проверено в DOM).
 * Было 240px, затем 190px: названия читались лучше, но суммарная ширина 9 колонок
 * (60 + 240 + 80 + 80 + 6×125 = 1210px) не помещалась в таблицу даже на широком экране,
 * и колонка «Долг» уходила за край. 170px убирает 70px и возвращает таблицу в видимую область.
 * На обычных строках данных (короткие номера помещений, «Сентябрь 2026») колонка просто шире:
 * у неё flex: 0.3, поэтому при достатке места ширина больше minWidth — значение влияет
 * только на узкие экраны.
 */
export const OSV_TITLE_COL_MIN_WIDTH = 170;
